import React, { useState } from "react";

import MarkerClusterGroup from '@changey/react-leaflet-markercluster';
import { MdmObject, useGetObjectLazyQuery } from "gql/generated/types";
import L, { DivIcon, LatLngBounds, LatLngTuple, divIcon } from "leaflet";
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { LayersControl, Marker, Popup, ZoomControl, useMap } from "react-leaflet";
import { Link, useParams } from "react-router-dom";

const defaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconAnchor: [12, 40],
  popupAnchor: [0, -40],
});

type ObjectMapContentProps = {
  objects: MdmObject[];
};

const ObjectMapContent: React.FC<ObjectMapContentProps> = ({ objects }) => {
  const map = useMap();
  map.fitBounds([
    [54.2, 23.5],
    [56.2, 24.5],
  ]);

  const markers = objects?.reduce((tuples, object) => {
    if (!object?.location?.latitude || !object?.location?.longitude) return tuples;
    tuples.push([object?.location?.latitude, object?.location?.longitude]);
    return tuples;
  }, [] as LatLngTuple[]);
  
  if (markers.length === 1) {
    map.setView(markers[0], 16);
  };

  if (markers.length > 1) {
    const bounds = new LatLngBounds(markers);
    const northEast = Object.values(bounds.getNorthEast()) as LatLngTuple;
    const southWest = Object.values(bounds.getSouthWest()) as LatLngTuple;
    map.fitBounds([northEast, southWest]);
  }
  const customIcon = divIcon({
    className: "custom-icon",
    html: `<div class="icon-text" title="${'N/A'}">${'N/A'}</div>`,
    iconAnchor: [37, 37],
    popupAnchor: [-18, -34],
  });

  const markerGroups: LayerProps[] = [
    {
      layerName: "Default Markers",
      markerIcon: defaultIcon,
      data: objects,
      checked: true
    },
    {
      layerName: "Data",
      markerIcon: customIcon,
      data: objects,
      checked: false
    }
  ]

  return (
    <div>
      <ZoomControl position="bottomright" />
      <LayersControl position="topright">
        {markerGroups.map(({ layerName, markerIcon, data, checked }) => (
          <Layer
            key={layerName}
            layerName={layerName}
            markerIcon={markerIcon}
            data={data}
            checked={checked}
          />
        ))}
      </LayersControl>
    </div>
  );
};

export default ObjectMapContent;

type LayerProps = {
  layerName: string;
  markerIcon: L.Icon<L.IconOptions> | DivIcon;
  data: MdmObject[];
  checked: boolean;
};

const Layer: React.FC<LayerProps> = ({ layerName, markerIcon, data, checked }) => {
  const mapMarkers = data?.map(object => (
    <ObjectMarker key={`${object.id}-${layerName}`} object={object} markerIcon={markerIcon} />
  ));
  return (
    <LayersControl.BaseLayer checked={checked} name={layerName}>
      <MarkerClusterGroup maxClusterRadius={60}>{mapMarkers}</MarkerClusterGroup>
    </LayersControl.BaseLayer>
  );
}

type ObjectMarkerProps = {
  markerIcon: L.Icon<L.IconOptions> | DivIcon;
  object: MdmObject;
};

const ObjectMarker: React.FC<ObjectMarkerProps> = ({ object, markerIcon }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { projectId } = useParams();

  const [getObject, {data, loading}] = useGetObjectLazyQuery();
  const objectData = data?.mdmObjectById ?? object;

  if (!objectData.location?.latitude || !objectData.location?.longitude) return null;
  const position: LatLngTuple = [objectData.location.latitude, objectData.location.longitude];
  return (
    <Marker
      key={objectData.id}
      position={position}
      icon={markerIcon}
      eventHandlers={{
        dblclick: () => {
          setDialogOpen(true);
          getObject({ variables: { id: object.id! } });
        },
      }}
    >
      <Dialog
        header={`${objectData.title} - ${object.address?.fullAddress}`}
        visible={dialogOpen}
        onHide={() => setDialogOpen(false)}
        modal
        style={{ minWidth: "500px", minHeight: "400px" }}
      >
        {loading ? (
          <ProgressSpinner className="flex mt-6" style={{ overflow: "hidden" }} />
        ) : (
          <div>
            <p>{`latitude: ${objectData.location?.latitude}`}</p>
            <p>{`longitude: ${objectData.location?.longitude}`}</p>
          </div>
        )}
      </Dialog>
      <Popup className="text-base">
        <Link
          to={`/${projectId}/objects/${objectData.id}`}
          className="cursor-pointer hover:underline"
        >
          {objectData.title}
        </Link>
        <div>{object.address?.fullAddress}</div>
      </Popup>
    </Marker>
  );
}
